import CT from '@marketplace-ai/tags';
import { useSelector } from '@xstate/react';
import { FormProvider } from 'react-hook-form';
import { FormSection, RichTextArea } from '~/components';
import { useCustomForm } from '~/hooks/useCustomForm';
import type { ListingContract } from '~/types/hlx/msaasDetail';
import { useMsaasListingSearchRef } from '../../marketSelectors';

const TAGS = CT.listingSearch.contractTerms;

type ContractTermsDetail = Partial<
  Pick<ListingContract, 'bonusesTxt' | 'adminCostsTxt' | 'infrastructureTxt' | 'extraTxt'>
>;

/**
 * [MCV-1955] The read-only contract terms, from the listing detail alone so the provider Job
 * listing view shows the same thing as B2B search. The form seeds its defaults once; callers
 * remount it (`key`) when the listing changes.
 */
export const ListingContractTerms = ({
  detail,
  dataCy,
}: {
  detail: ContractTermsDetail | null | undefined;
  dataCy?: string;
}) => {
  const methods = useCustomForm({
    defaultValues: {
      bonusesTxt: detail?.bonusesTxt,
      adminCostsTxt: detail?.adminCostsTxt,
      infrastructureTxt: detail?.infrastructureTxt,
      extraTxt: detail?.extraTxt,
    },
  });

  return (
    <div className="b2b-contract-terms min-w-0 text-left" data-cy={dataCy}>
      <h3 className="text-xl font-bold leading-snug mb-4 mt-6">Contract Terms</h3>
      <FormProvider {...methods}>
        <FormSection componentsPerLine={1}>
          <RichTextArea
            name="bonusesTxt"
            disabled
            label="Bonuses & Incentives"
            cyTag={TAGS.bonusesTxt}
          />
          <RichTextArea
            name="adminCostsTxt"
            cyTag={TAGS.adminCostsTxt}
            disabled
            label="Administrative Costs"
          />
          <RichTextArea
            name="infrastructureTxt"
            cyTag={TAGS.infrastructureTxt}
            disabled
            label="Infrastructure Requirements"
          />
          <RichTextArea name="extraTxt" disabled label="Other Terms" cyTag={TAGS.extraTxt} />
        </FormSection>
      </FormProvider>
    </div>
  );
};

export const ContractTerms = () => {
  const { msaasListingsSearchRef } = useMsaasListingSearchRef();
  const context = useSelector(msaasListingsSearchRef, (state) => state?.context);
  if (!msaasListingsSearchRef || !context) return null;

  return <ListingContractTerms detail={context.listing?.detail} />;
};
